import { useEffect, useState } from "react";
import { Post, useGetLegacySocialPosts } from "../../api/LegacyReportsApi";
import { LegacyReportSummaryItem } from "../../api/LegacyReportsApi";
import MonthlyList from "../common/MonthlyList";
import LegacyPostCard from "./LegacyPostCard";

function LegacyPostView({
  report,
  enabledPostTypes,
  enabledBehaviors,
  enabledKeywords,
}: {
  report: LegacyReportSummaryItem;
  enabledPostTypes: string[];
  enabledBehaviors: string[];
  enabledKeywords: string[];
}) {
  const [allPosts, setAllPosts] = useState<Post[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const { mutateAsync: getPosts, isLoading: isInitialLoading } =
    useGetLegacySocialPosts(report.reportId);

  // Load initial posts
  useEffect(() => {
    const loadPosts = async () => {
      const posts = await getPosts({ reportUUID: report.reportId, page: 1 });
      setAllPosts(posts);
      setHasMore(posts.length === 20);
    };
    loadPosts();
  }, [report.reportId, getPosts]);

  // Handle scroll loading
  useEffect(() => {
    const handleScroll = async () => {
      if (isLoading || !hasMore) return;

      const scrolledToBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 1000;

      if (scrolledToBottom || filteredPosts.length < 20) {
        setIsLoading(true);
        let newPosts: Post[] = [];
        let nextPage = page;

        do {
          nextPage += 1;
          const fetchedPosts = await getPosts({
            reportUUID: report.reportId,
            page: nextPage,
          });
          newPosts = [...newPosts, ...fetchedPosts];
          setAllPosts((prev) => [...prev, ...fetchedPosts]);
          setPage(nextPage);
          setHasMore(fetchedPosts.length === 20);
          if (fetchedPosts.length < 20) break;
        } while (newPosts.length > 0 && filteredPosts.length < 20);

        setIsLoading(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [
    getPosts,
    hasMore,
    isLoading,
    page,
    report.reportId,
    filteredPosts.length,
  ]);

  useEffect(() => {
    if (!allPosts || allPosts.length === 0) {
      setFilteredPosts([]);
      return;
    }

    const filteredPosts = allPosts.filter((post) => {
      const postTypeNotFiltered = enabledPostTypes.includes(
        post.postType.toUpperCase()
      );
      const hasEnabledBehavior = enabledBehaviors.some((behavior) =>
        post.flaggedSensitivities.includes(behavior)
      );
      const hasEnabledKeyword = enabledKeywords.some((keyword) =>
        [...post.flaggedKeywords, ...post.flaggedStemmableKeywords].includes(
          keyword
        )
      );
      return postTypeNotFiltered && (hasEnabledBehavior || hasEnabledKeyword);
    });

    setFilteredPosts(filteredPosts);
  }, [
    allPosts,
    enabledBehaviors,
    enabledKeywords,
    enabledPostTypes,
    setFilteredPosts,
  ]);

  if (isInitialLoading && !allPosts.length) {
    return (
      <div className="p-4 mb-4 rounded">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-[#E0E8E8] rounded w-1/4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-[#E0E8E8] rounded"></div>
            <div className="h-20 bg-[#E0E8E8] rounded"></div>
            <div className="h-20 bg-[#E0E8E8] rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 mb-4 rounded">
      <h2 className="text-[15px] tracking-[0.3px] text-[#84969f]">Posts</h2>
      <div className="mt-4">
        {filteredPosts.length > 0 ? (
          <MonthlyList
            items={filteredPosts}
            renderItem={(post) => <LegacyPostCard post={post} />}
            getItemDate={(post) => new Date(post.date)}
            columns={2}
          />
        ) : (
          <div className="text-gray-500">
            No posts matching the selected filters
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center py-4">
            <div className="animate-pulse text-gray-500">
              Loading more posts...
            </div>
          </div>
        )}

        {!hasMore && !isLoading && (
          <div className="flex justify-center py-4">
            <p className="text-gray-500">All posts loaded</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default LegacyPostView;
